import { memo, PropsWithChildren, useMemo } from 'react'

import { ForwardToInbox, Groups, Person } from '@mui/icons-material'
import { Box } from '@mui/material'
import { useMatchRoute, useNavigate } from '@tanstack/react-router'

import { Tab } from '@shared/components/MaterialUIEnhancements/Tabs'
import { StatelessTabs } from '@shared/components/MaterialUIEnhancements/Tabs/StatelessTabs'
import { StaticRouteParameter } from 'config/routing'
import {
  workspaceSettingsPermissionsRoute,
  workspaceSettingsPermissionsTeamsRoute,
  workspaceSettingsPermissionsUserRoute,
} from 'pages/NWPlatform/Workspaces/Workspace/Settings'

type IconTabProps = {
  id: string
}

const IconTab = memo(function IconTabRaw({ id, children }: PropsWithChildren<IconTabProps>) {
  return (
    <Tab
      sx={{
        fontSize: 20,
        width: 80,
      }}
      id={id}
      ripple
    >
      {children}
    </Tab>
  )
})

const ContentTabsRaw = () => {
  const navigate = useNavigate({ from: workspaceSettingsPermissionsRoute.fullPath })
  const matchRoute = useMatchRoute()

  const userPath = matchRoute({ to: workspaceSettingsPermissionsUserRoute.fullPath })
  const teamPath = matchRoute({ to: workspaceSettingsPermissionsTeamsRoute.fullPath })
  const invitationPath = matchRoute({
    to: '/organizations/$organizationId/environments/$environmentId/workspaces/$workspaceId/settings/permissions/invitations',
  })

  const paths = useMemo(
    () => ({
      [StaticRouteParameter.USER]: workspaceSettingsPermissionsUserRoute.to,
      [StaticRouteParameter.TEAMS]: workspaceSettingsPermissionsTeamsRoute.to,
      [StaticRouteParameter.INVITATIONS]:
        '/organizations/$organizationId/environments/$environmentId/workspaces/$workspaceId/settings/permissions/invitations',
    }),
    [],
  )

  const derivedActiveTab = userPath
    ? StaticRouteParameter.USER
    : teamPath
      ? StaticRouteParameter.TEAMS
      : invitationPath
        ? StaticRouteParameter.INVITATIONS
        : undefined

  return (
    <Box sx={{ mb: 1, width: 'min-content' }}>
      <StatelessTabs
        variant="flat"
        value={derivedActiveTab}
        setActiveTab={(id) => {
          if (id) navigate({ to: paths[id as keyof typeof paths] })
        }}
      >
        <IconTab id={StaticRouteParameter.USER}>
          <Person fontSize="inherit" />
        </IconTab>
        <IconTab id={StaticRouteParameter.TEAMS}>
          <Groups fontSize="inherit" />
        </IconTab>
        <IconTab id={StaticRouteParameter.INVITATIONS}>
          <ForwardToInbox fontSize="inherit" />
        </IconTab>
      </StatelessTabs>
    </Box>
  )
}

export const ContentTabs = memo(ContentTabsRaw)
